import {
  TreemapNodeKind,
  type TreemapTreeNode,
  type TreemapRect,
  type TreemapFlowNode,
  type TreemapClassStyle,
} from './treemap-types';
import { DEPTH_COLORS, HEADER_HEIGHT, SECTION_PADDING } from './treemap-constants';

const TREEMAP_CELL_NODE_TYPE = 'treemapCell';

export interface LayoutResult {
  nodes: TreemapFlowNode[];
  graphWidth: number;
  graphHeight: number;
}

interface RowItem {
  index: number;
  area: number;
}

interface SectionChildLayout {
  inner: TreemapRect;
  tileable: TreemapTreeNode[];
}

function isRenderableRect(rect: TreemapRect): boolean {
  return Number.isFinite(rect.x) && Number.isFinite(rect.y) && rect.w > 0 && rect.h > 0;
}

// "Worst" aspect ratio of a row of areas laid along a side of length
// `shorterSide` (Bruls/Huizing/van Wijk). Lower is squarer.
function worstRatio(areas: number[], shorterSide: number): number {
  if (areas.length === 0 || shorterSide <= 0) {
    return Infinity;
  }
  const sum = areas.reduce((total, area) => total + area, 0);
  if (sum <= 0) {
    return Infinity;
  }
  const max = areas.reduce((largest, area) => Math.max(largest, area), -Infinity);
  const min = areas.reduce((smallest, area) => Math.min(smallest, area), Infinity);
  const sumSquared = sum * sum;
  const sideSquared = shorterSide * shorterSide;
  return Math.max((sideSquared * max) / sumSquared, sumSquared / (sideSquared * min));
}

// Lay a finalized row along the shorter side of `remaining`, writing each item's
// rect into `result` by original index, then shrink `remaining` by the consumed strip.
function layoutRow(input: {
  rowItems: RowItem[];
  remaining: TreemapRect;
  result: TreemapRect[];
}): void {
  const { rowItems, remaining, result } = input;
  const rowSum = rowItems.reduce((total, item) => total + item.area, 0);
  if (rowSum <= 0) {
    return;
  }
  const shorterSide = Math.min(remaining.w, remaining.h);
  if (shorterSide <= 0) {
    return;
  }
  const strip = rowSum / shorterSide;

  if (remaining.w >= remaining.h) {
    // Wide rect → stack the row as a vertical column on the left edge.
    let offsetY = remaining.y;
    rowItems.forEach((item) => {
      const cellHeight = item.area / strip;
      result[item.index] = { x: remaining.x, y: offsetY, w: strip, h: cellHeight };
      offsetY += cellHeight;
    });
    remaining.x += strip;
    remaining.w -= strip;
    return;
  }

  // Tall rect → lay the row left-to-right across the top edge.
  let offsetX = remaining.x;
  rowItems.forEach((item) => {
    const cellWidth = item.area / strip;
    result[item.index] = { x: offsetX, y: remaining.y, w: cellWidth, h: strip };
    offsetX += cellWidth;
  });
  remaining.y += strip;
  remaining.h -= strip;
}

// Squarified tiling: assign each child a rect whose area ∝ its value. Rects are
// returned in the SAME order as `children`.
function squarify(input: { children: TreemapTreeNode[]; rect: TreemapRect }): TreemapRect[] {
  const { children, rect } = input;
  const result: TreemapRect[] = children.map(() => ({ x: rect.x, y: rect.y, w: 0, h: 0 }));

  const total = children.reduce((sum, child) => sum + child.value, 0);
  if (total <= 0 || rect.w <= 0 || rect.h <= 0) {
    return result;
  }

  const scale = (rect.w * rect.h) / total;
  const items: RowItem[] = children
    .map((child, index) => ({ index, area: child.value * scale }))
    .sort((first, second) => second.area - first.area);

  const remaining: TreemapRect = { ...rect };
  let row: RowItem[] = [];
  let rowAreas: number[] = [];

  items.forEach((item) => {
    const shorterSide = Math.min(remaining.w, remaining.h);
    const currentWorst = worstRatio(rowAreas, shorterSide);
    const nextWorst = worstRatio([...rowAreas, item.area], shorterSide);

    if (row.length === 0 || nextWorst <= currentWorst) {
      row.push(item);
      rowAreas.push(item.area);
      return;
    }

    layoutRow({ rowItems: row, remaining, result });
    row = [item];
    rowAreas = [item.area];
  });

  if (row.length > 0) {
    layoutRow({ rowItems: row, remaining, result });
  }

  return result;
}

function toFlowNode(input: {
  node: TreemapTreeNode;
  rect: TreemapRect;
  colorIndex: number;
  classDefs: Map<string, TreemapClassStyle>;
}): TreemapFlowNode {
  const { node, rect, colorIndex, classDefs } = input;
  const classStyle = node.className ? classDefs.get(node.className) ?? null : null;

  return {
    id: node.id,
    type: TREEMAP_CELL_NODE_TYPE,
    position: { x: rect.x, y: rect.y },
    width: rect.w,
    height: rect.h,
    draggable: false,
    selectable: false,
    connectable: false,
    // Ancestors paint first; descendants on top. Pre-order push + zIndex agree.
    zIndex: node.depth,
    data: {
      nodeId: node.id,
      label: node.label,
      value: node.value,
      kind: node.kind,
      depth: node.depth,
      colorIndex,
      classStyle,
      showValue: true,
    },
    style: { width: rect.w, height: rect.h },
  };
}

function getSectionInnerRect(rect: TreemapRect): TreemapRect {
  return {
    x: rect.x + SECTION_PADDING,
    y: rect.y + HEADER_HEIGHT,
    w: rect.w - SECTION_PADDING * 2,
    h: rect.h - HEADER_HEIGHT - SECTION_PADDING,
  };
}

function getTileableChildren(node: TreemapTreeNode): TreemapTreeNode[] {
  return node.children.filter((child) => child.value > 0);
}

function getSectionChildLayout(input: {
  node: TreemapTreeNode;
  rect: TreemapRect;
}): SectionChildLayout | null {
  if (input.node.kind !== TreemapNodeKind.Section) {
    return null;
  }

  const inner = getSectionInnerRect(input.rect);
  if (!isRenderableRect(inner)) {
    return null;
  }

  const tileable = getTileableChildren(input.node);
  return tileable.length > 0 ? { inner, tileable } : null;
}

// Emit a node for `node` at `rect`, then (if a section) reserve a header strip
// and tile its children into the remaining area. Pre-order push keeps ancestors
// ahead of descendants in the array.
function layoutNode(input: {
  node: TreemapTreeNode;
  rect: TreemapRect;
  colorIndex: number;
  classDefs: Map<string, TreemapClassStyle>;
  sink: TreemapFlowNode[];
}): void {
  const { node, rect, colorIndex, classDefs, sink } = input;

  if (!isRenderableRect(rect)) {
    return;
  }

  sink.push(toFlowNode({ node, rect, colorIndex, classDefs }));

  const childLayout = getSectionChildLayout({ node, rect });
  if (!childLayout) {
    return;
  }

  const childRects = squarify({ children: childLayout.tileable, rect: childLayout.inner });
  childLayout.tileable.forEach((child, index) => {
    layoutNode({ node: child, rect: childRects[index], colorIndex, classDefs, sink });
  });
}

// The synthetic root is not rendered: its children (top-level sections) tile the
// whole canvas, each anchoring a color family by index.
export function buildTreemapLayout(input: {
  root: TreemapTreeNode;
  width: number;
  height: number;
  classDefs: Map<string, TreemapClassStyle>;
}): LayoutResult {
  const { root, width, height, classDefs } = input;
  const sink: TreemapFlowNode[] = [];

  const topChildren = root.children.filter((child) => child.value > 0);
  if (topChildren.length > 0) {
    const fullRect: TreemapRect = { x: 0, y: 0, w: width, h: height };
    const topRects = squarify({ children: topChildren, rect: fullRect });
    topChildren.forEach((child, index) => {
      layoutNode({
        node: child,
        rect: topRects[index],
        colorIndex: index % DEPTH_COLORS.length,
        classDefs,
        sink,
      });
    });
  }

  return { nodes: sink, graphWidth: width, graphHeight: height };
}

// ---------------------------------------------------------------------------
// Tree helpers — drill-down breadcrumbs, hover highlight sets, keyboard nav.
// ---------------------------------------------------------------------------

export function findTreemapNodeById(input: {
  root: TreemapTreeNode;
  nodeId: string;
}): TreemapTreeNode | null {
  const { root, nodeId } = input;
  if (root.id === nodeId) {
    return root;
  }
  for (const child of root.children) {
    const found = findTreemapNodeById({ root: child, nodeId });
    if (found) {
      return found;
    }
  }
  return null;
}

export function buildParentMap(root: TreemapTreeNode): Map<string, string> {
  const parentMap = new Map<string, string>();
  const walk = (node: TreemapTreeNode, parentId: string | null): void => {
    if (parentId) {
      parentMap.set(node.id, parentId);
    }
    node.children.forEach((child) => walk(child, node.id));
  };
  walk(root, null);
  return parentMap;
}

// Ancestor chain from `nodeId` up to (and including) the highest rendered
// ancestor — excludes the synthetic root (which has no rendered node).
export function getAncestorPath(input: {
  nodeId: string;
  parentMap: Map<string, string>;
  rootId: string;
}): string[] {
  const { nodeId, parentMap, rootId } = input;
  const ancestors: string[] = [];
  const visited = new Set<string>();
  let currentNodeId: string | undefined = nodeId;

  while (currentNodeId && currentNodeId !== rootId && !visited.has(currentNodeId)) {
    ancestors.push(currentNodeId);
    visited.add(currentNodeId);
    currentNodeId = parentMap.get(currentNodeId);
  }

  return ancestors;
}

export function collectSubtreeIds(node: TreemapTreeNode): string[] {
  const ids: string[] = [];
  const walk = (current: TreemapTreeNode): void => {
    ids.push(current.id);
    current.children.forEach((child) => walk(child));
  };
  walk(node);
  return ids;
}

export function findSiblingNodeId(input: {
  root: TreemapTreeNode;
  nodeId: string;
  parentMap: Map<string, string>;
  offset: 1 | -1;
}): string | null {
  const parentNode = getSiblingParentNode(input);
  return parentNode
    ? getOffsetSiblingId({
        siblings: parentNode.children,
        nodeId: input.nodeId,
        offset: input.offset,
      })
    : null;
}

function getSiblingParentNode(input: {
  root: TreemapTreeNode;
  nodeId: string;
  parentMap: Map<string, string>;
}): TreemapTreeNode | null {
  const { root, nodeId, parentMap } = input;
  const parentNodeId = parentMap.get(nodeId);
  if (!parentNodeId) {
    return null;
  }

  return findTreemapNodeById({ root, nodeId: parentNodeId });
}

function getOffsetSiblingId(input: {
  siblings: TreemapTreeNode[];
  nodeId: string;
  offset: 1 | -1;
}): string | null {
  const siblingIndex = input.siblings.findIndex((child) => child.id === input.nodeId);
  if (siblingIndex === -1) {
    return null;
  }

  const targetIndex = siblingIndex + input.offset;
  return input.siblings[targetIndex]?.id ?? null;
}
